/// <amd-module name="Repos/ContentPreview/Markdown/MarkdownLinkingService" />
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { UserContentAnchorHelper } from "VSS/Features/Markdown/Utils/UserContentAnchorHelper";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";

export interface IMarkdownLinkingService extends IVssService {
    /**
     * Gets the observable anchor value.
     */
    getAnchor: () => IReadonlyObservableValue<string>;

    /**
     * Tries to get the anchor value from the URL and scrolls associated
     * anchor element into the view.
     */
    navigateToAnchorFromHistory: () => void;

    /**
     * Scrolls the anchor element specified by the anchor value into the view.
     *
     * @param anchor Anchor to scroll to the top of the page.
     */
    navigateToAnchor: (anchor: string) => void;

    /**
     * Sets a new anchor value.
     */
    setAnchor: (anchor: string) => void;

    /**
     * Navigates to the specified file path using FPS.
     */
    setRelativePath: (path: string, anchor?: string) => void;
}

class MarkdownLinkingService extends VssService implements IMarkdownLinkingService {
    private anchor = new ObservableValue("");

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageStateHistoryService.registerObservable(this.anchor, "anchor", HistoryBehavior.replace);
    }

    public navigateToAnchorFromHistory(): void {
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageStateHistoryService.getProjectedState();
        this.navigateToAnchor(historyState["anchor"] || "");
    }

    public navigateToAnchor(anchor: string): void {
        // Once extracted from any URL, anchor should arrive here decoded, but IDs are encoded
        anchor = encodeURIComponent(anchor);

        // Try to get generic inline anchor first
        let anchorElement = document.getElementById(anchor);

        if (!anchorElement) {
            // If this is not a generic anchor see this is a table-of-contents link
            anchorElement = document.getElementById(UserContentAnchorHelper.convertAnchorName(anchor));
        }

        if (anchorElement) {
            // Scroll into the anchor element if found any
            anchorElement.scrollIntoView();
        }
    }

    public getAnchor(): IReadonlyObservableValue<string> {
        return this.anchor;
    }

    public setAnchor(anchor: string): void {
        this.anchor.value = anchor;
    }

    public setRelativePath(path: string, anchor?: string): void {
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const basePath = pageStateHistoryService.getProjectedState()["path"];

        // Normalize relative path first
        path = this.normalizePath(path, basePath);

        // Generate the file content url using the normalized path
        const gitRepository = getGitRepository(this.pageContext)!;
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const relativeUrl = reposRoutingService.getFilesUrl(gitRepository.name, gitRepository.project.name, path, undefined, anchor);

        FastPageSwitch(this.pageContext, relativeUrl, true);
    }

    private normalizePath(path: string, basePath: string = "/"): string {
        const lastSlash = basePath.lastIndexOf("/");
        if (lastSlash >= 0) {
            basePath = basePath.substring(0, lastSlash);
        }

        // Ensure path starts with /
        if (basePath.charAt(0) !== "/") {
            basePath = "/" + basePath;
        }

        if (path.startsWith("./")) {
            path = combineUrlPaths(basePath, path.substr("./".length));
        } else if (!path.startsWith("/")) {
            path = combineUrlPaths(basePath, path);
        }

        return path;
    }
}

const serviceName = "IMarkdownLinkingService";
Services.add(serviceName, { serviceFactory: MarkdownLinkingService });

export function getService(pageContext: IVssPageContext): IMarkdownLinkingService {
    return pageContext.getService<IMarkdownLinkingService>(serviceName);
}
